import { VideoCameraIcon, VideoCameraSlashIcon } from "@heroicons/react/24/outline";
import { ArrowsPointingInIcon, ArrowsPointingOutIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";

import { Select, Tooltip } from "@fronterahealth/frontera-ui-components";

import { GoalStatusEnums, useUpdateProgrammingGoalMutation } from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";
import { GoalCard } from "@pages/VideoSessionsV2/components/Moments/VideoMoments/components/GoalCard";
import { OtherMomentCard } from "@pages/VideoSessionsV2/components/Moments/VideoMoments/components/OtherMomentCard";
import { NoData } from "@pages/VideoSessionsV2/components/NoData/NoData";
import { DataLoading } from "@pages/VideoSessionsV2/components/Utils/DataLoading";
import { GoalMoment, useVideoSessionData } from "@providers/VideoSessionProviderV2";

const SectionDivider = ({
  title,
  count,
  isCollapsed,
  onToggle,
}: {
  title: string;
  count: number;
  isCollapsed: boolean;
  onToggle: () => void;
}) => (
  <div
    className="flex items-center gap-2 px-1 py-2 cursor-pointer hover:bg-limestone-50 transition-colors rounded-lg group"
    onClick={onToggle}
  >
    <ChevronRightIcon
      className={clsx("w-4 h-4 text-limestone-600 transition-transform duration-200", !isCollapsed && "rotate-90")}
    />
    <span className="text-xs text-limestone-600">
      {title} ({count})
    </span>
    <div className="flex-1 h-px bg-limestone-200" />
  </div>
);

interface VideoMomentsProps {
  videoSeeking: boolean;
  activeMomentId: string | null;
  onClick: (startTime: number, endTime: number, momentId: string) => void;
}

export const VideoMoments: React.FC<VideoMomentsProps> = ({ onClick }) => {
  const {
    goalMoments,
    mediaGoalMomentsQuery,
    expandAllGoals,
    setExpandAllGoals,
    onlyMoments,
    setOnlyMoments,
    selectedGoalStatus,
    setSelectedGoalStatus,
    mediaClassificationsData,
  } = useVideoSessionData();

  const { mutate: updateGoal, isPending } = useUpdateProgrammingGoalMutation({
    onSuccess: () => mediaGoalMomentsQuery.refetch(),
  });

  const [pinnedSectionCollapsed, setPinnedSectionCollapsed] = useState<boolean>(false);
  const [unpinnedSectionCollapsed, setUnpinnedSectionCollapsed] = useState<boolean>(false);

  const momentRef = useRef<HTMLDivElement>(null);

  const { activeMomentId } = useVideoSessionData();

  // Scroll to active moment
  useEffect(() => {
    if (activeMomentId && momentRef.current) {
      const activeElement = momentRef.current.querySelector(`[data-moment-id="${activeMomentId}"]`);
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [activeMomentId]);

  const pinnedGoals = goalMoments.filter((g) => g.isPinned);
  const unPinnedGoals = goalMoments.filter((g) => !g.isPinned);
  const hasBothSections = pinnedGoals.length > 0 && unPinnedGoals.length > 0;

  const handlePinToggle = (goal: GoalMoment) => {
    updateGoal({
      learnerId: goal.learner.id,
      input: {
        id: goal.id,
        isPinned: !goal.isPinned,
      },
    });
  };

  return (
    <div
      ref={momentRef}
      className="bg-limestone-50 p-4 gap-3 grid grid-flow-row auto-rows-max max-lg:min-h-[calc(100vh-18rem)] lg:overflow-y-auto overflow-x-hidden"
    >
      <div className="flex flex-col gap-2 w-full overflow-hidden min-h-[calc(100vh-18rem)]">
        <div className="flex items-center gap-2 px-2">
          <div className="flex flex-row gap-2 items-center">
            <Select
              title=""
              items={Object.values(GoalStatusEnums).map((status) => ({
                primary: convertReadableString(status),
                id: status,
              }))}
              setSelected={(selected) => {
                setSelectedGoalStatus(selected.id as GoalStatusEnums | undefined);
              }}
              selected={
                selectedGoalStatus
                  ? {
                      primary: convertReadableString(selectedGoalStatus),
                      id: selectedGoalStatus,
                    }
                  : undefined
              }
              placeholderText="Goal Status"
              className="max-h-14! min-w-[12.5vw]! max-w-[12.5vw]!"
            />
            {selectedGoalStatus && (
              <XMarkIcon
                className="h-6 w-6 cursor-pointer text-text-secondary"
                onClick={() => {
                  setSelectedGoalStatus(undefined);
                }}
              ></XMarkIcon>
            )}
          </div>

          <div className="flex flex-1 justify-end gap-2">
            {onlyMoments ? (
              <Tooltip text="Show All" showInfoIcon={false}>
                <VideoCameraSlashIcon
                  className="h-6 w-6 cursor-pointer text-text-secondary"
                  onClick={() => {
                    setOnlyMoments(false);
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip text="Show Moments Only" showInfoIcon={false}>
                <VideoCameraIcon
                  className="h-6 w-6 cursor-pointer text-text-secondary"
                  onClick={() => {
                    setOnlyMoments(true);
                  }}
                />
              </Tooltip>
            )}

            {expandAllGoals ? (
              <Tooltip text="Collapse All" showInfoIcon={false}>
                <ArrowsPointingInIcon
                  className="h-6 w-6 cursor-pointer text-text-secondary"
                  onClick={() => {
                    setExpandAllGoals(false);
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip text="Expand All" showInfoIcon={false}>
                <ArrowsPointingOutIcon
                  className="h-6 w-6 cursor-pointer text-text-secondary"
                  onClick={() => {
                    setExpandAllGoals(true);
                  }}
                />
              </Tooltip>
            )}
          </div>
        </div>

        {mediaGoalMomentsQuery.isLoading ? (
          <DataLoading text="Activity Loading" />
        ) : goalMoments?.length || mediaClassificationsData?.length ? (
          hasBothSections ? (
            <>
              <SectionDivider
                title="Pinned"
                count={pinnedGoals.length}
                isCollapsed={pinnedSectionCollapsed}
                onToggle={() => setPinnedSectionCollapsed(!pinnedSectionCollapsed)}
              />
              <div
                className={clsx(
                  "flex flex-col gap-3 transition-all duration-300 w-full",
                  pinnedSectionCollapsed ? "max-h-0 overflow-hidden" : "max-h-none overflow-hidden",
                )}
              >
                {pinnedGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    onClick={onClick}
                    onPinToggle={handlePinToggle}
                    isPending={isPending}
                    expandAll={expandAllGoals}
                  />
                ))}
              </div>

              <SectionDivider
                title="Other moments"
                count={unPinnedGoals.length + (mediaClassificationsData?.length || 0)}
                isCollapsed={unpinnedSectionCollapsed}
                onToggle={() => setUnpinnedSectionCollapsed(!unpinnedSectionCollapsed)}
              />
              <div
                className={clsx(
                  "flex flex-col gap-3 transition-all duration-300 w-full",
                  unpinnedSectionCollapsed ? "max-h-0 overflow-hidden" : "max-h-none overflow-hidden",
                )}
              >
                {mediaClassificationsData.length ? (
                  <OtherMomentCard onClick={onClick} expandAll={expandAllGoals} />
                ) : null}
                {unPinnedGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    onClick={onClick}
                    onPinToggle={handlePinToggle}
                    isPending={isPending}
                    expandAll={expandAllGoals}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              {mediaClassificationsData.length ? (
                <OtherMomentCard onClick={onClick} expandAll={expandAllGoals} />
              ) : null}
              {/*  No pinned goals or all goals are pinned - render normally */}
              {goalMoments.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onClick={onClick}
                  onPinToggle={handlePinToggle}
                  isPending={isPending}
                  expandAll={expandAllGoals}
                />
              ))}
            </>
          )
        ) : (
          <NoData
            title="No Moments Detected"
            description="It looks like no moments were detected in this video, or we haven't processed this video yet."
          />
        )}
      </div>
    </div>
  );
};
